import { config as appConfig } from 'config';
import { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import { Store } from '@reduxjs/toolkit';
import { AppDispatch, AppState } from 'app/store';
import { signOutEffect } from 'app/store/Auth/effects';
import {
  deleteUserGuid,
  ensureDomain,
  getToken,
  getUserGuid,
} from 'app/utils/helpers';
import {
  IAxiosRetryConfig,
  isNetworkOrIdempotentRequestError,
} from 'axios-retry';
import { SECURITY_ERROR } from 'types/enums';

export const AXIOS_RETRY_CONFIG: IAxiosRetryConfig = {
  retries: 3,
  retryDelay: (retryCount) => (retryCount === 1 ? 0 : retryCount * 1500),
  retryCondition: (error: AxiosError<any>) =>
    error?.response?.data?.error?.retryable ??
    isNetworkOrIdempotentRequestError(error),
};

export const getClientHeader = () => {
  const client = appConfig.isElectronBuild ? 'desktop' : 'web';
  return {
    key: 'session-client',
    value: `session-client-studio-${client}@${appConfig.appVersion}`,
  };
};

export const axios401ResponseInterceptor = (store: Store<AppState>) => [
  (response: AxiosResponse) => response,
  (error: any) => {
    const dispatch = store.dispatch as AppDispatch;
    if (error?.response?.status === 401) {
      dispatch(signOutEffect({}));
    }
    return Promise.reject(error);
  },
];

export const axiosAuthHeaderRequestInterceptor = (store: Store<AppState>) => (
  config: AxiosRequestConfig
) => {
  const clientHeader = getClientHeader();
  const { apiKeyWeb, authenticated, account } = store.getState().auth;
  const extendedConfig = { ...config, headers: config.headers || {} };

  const reload = () => {
    deleteUserGuid();
    window.location.reload();
  };

  try {
    if (
      !appConfig.isElectronBuild &&
      authenticated &&
      getUserGuid() !== account?.userGuid
    ) {
      reload();
    }
  } catch (e: any) {
    if (e?.name === SECURITY_ERROR) {
      reload();
    }
  }

  if (ensureDomain(config.url || '')) {
    extendedConfig.headers[clientHeader.key] = clientHeader.value;

    if (apiKeyWeb) {
      extendedConfig.headers['x-api-key'] = apiKeyWeb;
    }
  }

  if (
    config.withCredentials &&
    (appConfig.isElectronBuild ||
      (appConfig.env === 'development' && window.location.protocol === 'http:'))
  ) {
    if (getToken()) {
      extendedConfig.headers.Authorization = getToken() || '';
    }
  }
  return extendedConfig;
};
